import React, { useState, useRef, useEffect } from 'react';
import { Share2, Copy, Check } from 'lucide-react';

export default function ShareButton({ url, title }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverH = 228;
      const popoverW = 180;
      const margin = 8;
      let top = rect.bottom + 4;
      if (top + popoverH > window.innerHeight - margin) top = rect.top - popoverH - 4;
      top = Math.max(margin, Math.min(top, window.innerHeight - popoverH - margin));
      let left = rect.right - popoverW;
      if (left < margin) left = rect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - popoverW - margin));
      setPopoverStyle({ top, left });
    }
    setOpen(true);
  };

  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || 'Check this out on IsItTrueNow');

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 1500);
    }).catch(() => {});
  };

  const shareLinks = [
    { label: 'Twitter / X', href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}` },
    { label: 'Facebook',    href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
    { label: 'Reddit',      href: `https://www.reddit.com/submit?url=${encoded}&title=${encodedTitle}` },
    { label: 'WhatsApp',    href: `https://wa.me/?text=${encodedTitle}%20${encoded}` },
  ];

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        title="Share"
        style={{
          background: '#ffffff',
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          color: '#475569',
          cursor: 'pointer',
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          lineHeight: 1.4,
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#94a3b8'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; }}
      >
        <Share2 size={12} />
        Share
      </button>

      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: popoverStyle.top,
            left: popoverStyle.left,
            zIndex: 9999,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '4px 0',
            minWidth: 180,
          }}
        >
          {shareLinks.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              style={{
                display: 'block',
                padding: '9px 14px',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
                fontFamily: 'var(--font-body)',
                textDecoration: 'none',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {label}
            </a>
          ))}
          <div style={{ borderTop: '1px solid var(--color-border)', margin: '2px 0' }} />
          <button
            onClick={copyLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              padding: '9px 14px',
              background: 'transparent',
              border: 'none',
              color: copied ? 'var(--color-verified)' : 'var(--color-text-secondary)',
              fontSize: 13,
              fontFamily: 'var(--font-body)',
              fontWeight: copied ? 600 : 400,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.1s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      )}
    </div>
  );
}
